import BusAlertIcon from '@mui/icons-material/BusAlert'
import { Box, Checkbox, CircularProgress, MenuItem, Stack, Typography } from '@mui/material'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import type { GeoJSONSource, Map as MaplibreMap } from 'maplibre-gl'
import { FeatureCollection, LineString, Point } from 'geojson'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import {
    useGetTripGeometryTripsTripIdGeometryGet,
    useGetTripTripsTripIdGet,
    useGetVehicleLocationsTripsTripIdVehiclesGet,
} from 'src/generated/brezavta/queries'

import AlertDrawer from 'src/components/AlertDrawer'
import { LineChip } from 'src/components/LineChip'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'
import SheetHeader from 'src/components/sheet/SheetHeader'
import TripArrivals from 'src/components/TripArrivals'
import { ICON_BASE_SIZE } from 'src/lib/constants'
import { mapSetLayoutProperty, mapSetPaintProperty, registerVehicleImages, colorToImageId, normalizeVehicleColor, darkenColor } from 'src/lib/util'
import { StopTime, VehicleLocation } from 'src/generated/brezavta/requests/types.gen'

export const Route = createFileRoute('/trip/$tripId')({ component: Trip })

const VEHICLE_LOCATION_REFRESH_INTERVAL = 3000
const AGENCY_ICON_WHITELIST = new Set(['lpp', 'ijpp', 'marprom', 'celebus', 'mokranj', 'mokrsko', 'monm', 'brezice', 'sevnica', 'movelenje', 'moms', 'postojna'])

const HIDDEN_ICON_SIZE = ICON_BASE_SIZE * 0.1
const FULL_ICON_SIZE = ICON_BASE_SIZE * 1
const TRIP_ICON_EXPAND_ZOOM = 16
const TRIP_BACKGROUND_ICON_SIZE_EXPRESSION = ['step', ['zoom'], HIDDEN_ICON_SIZE, TRIP_ICON_EXPAND_ZOOM, FULL_ICON_SIZE]
const TRIP_BACKGROUND_ICON_OPACITY_EXPRESSION = 0.25

const SOURCE_TRIP_POLYLINE = 'tripPolyline'
const SOURCE_TRIP_STOPS = 'tripStops'
const SOURCE_VEHICLES = 'vehicles'

const DEFAULT_STOP_ICON_SIZE = ['interpolate', ['linear'], ['zoom'], 10, ICON_BASE_SIZE * 0.1, 13, ICON_BASE_SIZE * 1]
const DEFAULT_PARKING_ICON_SIZE = ['interpolate', ['linear'], ['zoom'], 12, ICON_BASE_SIZE * 0.1, 13, ICON_BASE_SIZE * 1]
const DEFAULT_MICROMOBILITY_DETAIL_TEXT_SIZE = ['case', ['==', ['get', 'floating'], 1], 0, 11]

type LayerPropertyRule = {
    prefix: string
    property: string
    value: unknown
}

function applyPaintRules(mapInstance: MaplibreMap, rules: LayerPropertyRule[]) {
    for (const rule of rules) {
        mapSetPaintProperty(mapInstance, rule.prefix, rule.property, rule.value)
    }
}

function applyLayoutRules(mapInstance: MaplibreMap, rules: LayerPropertyRule[]) {
    for (const rule of rules) {
        mapSetLayoutProperty(mapInstance, rule.prefix, rule.property, rule.value)
    }
}

export function TripPage({ tripId, queryDate }: { tripId: string, queryDate: string }) {
    const { map, mapLoaded } = useContext(MapRenderContext)
    const { t } = useTranslation()
    const today = DateTime.local({ zone: 'Europe/Ljubljana' }).toFormat('yyyyMMdd')
    const isToday = queryDate === today

    const [isFollowing, setIsFollowing] = useState(true)
    const lastSourceSignatureRef = useRef<Record<string, string>>({})
    const stopCoordinateCacheRef = useRef<Map<string, [number, number]>>(new Map())
    const registeredVehicleImagesRef = useRef<Set<string>>(new Set())

    const { data: tripData, error: tripDataError, isLoading: tripDataLoading, refetch: refetchTrip } = useGetTripTripsTripIdGet({
        path: { trip_id: tripId },
        query: { date: queryDate },
    })
    const { data: polylineData } = useGetTripGeometryTripsTripIdGeometryGet({ path: { trip_id: tripId } })
    const { data: vehicleLocationData, refetch: refetchVehicles } = useGetVehicleLocationsTripsTripIdVehiclesGet({
        path: { trip_id: tripId },
    }, undefined, { enabled: isToday })

    // Auto refreshing

    useEffect(() => {
        const interval = setInterval(() => {
            refetchTrip()
            if (isToday) {
                refetchVehicles()
            }
        }, VEHICLE_LOCATION_REFRESH_INTERVAL)

        return () => clearInterval(interval)
    }, [isToday, refetchTrip, refetchVehicles])

    // Polyline

    const setGeoJsonData = useCallback((sourceId: string, data: FeatureCollection<Point | LineString>, signature?: string) => {
        if (signature && lastSourceSignatureRef.current[sourceId] === signature) return

        const source = map?.current?.getSource(sourceId) as GeoJSONSource | undefined

        if (!source) {
            console.warn(`GeoJSON source "${sourceId}" not found on map`)
            return
        }

        source.setData(data)

        if (signature)
            lastSourceSignatureRef.current[sourceId] = signature
    }, [map])

    const hydrateStopCoordinates = useCallback((stopIds: string[]) => {
        const missingStopIds = stopIds.filter(stopId => !stopCoordinateCacheRef.current.has(stopId))
        if (missingStopIds.length === 0) return

        const mapInstance = map?.current
        if (!mapInstance) return

        const sources = Object.keys(mapInstance.getStyle()?.sources || {}).filter(sourceId => sourceId.startsWith('stops-circles-'))

        for (const sourceId of sources) {
            const sourceFeatures = mapInstance.querySourceFeatures(sourceId)
            for (const feature of sourceFeatures) {
                const stopId = feature.properties?.id as string | undefined
                const geometry = feature.geometry as Point | undefined
                const coordinates = geometry?.type === 'Point' ? (geometry.coordinates as [number, number]) : null
                if (!stopId || !coordinates) continue
                stopCoordinateCacheRef.current.set(stopId, coordinates)
            }
        }
    }, [map])

    const updatePolylineLayer = useCallback(() => {
        if (polylineData) {
            const first = polylineData.coordinates[0]
            const last = polylineData.coordinates[polylineData.coordinates.length - 1]
            const polylineSignature = `${polylineData.properties.color}:${polylineData.coordinates.length}:${first?.[0] || ''},${first?.[1] || ''}:${last?.[0] || ''},${last?.[1] || ''}`

            setGeoJsonData('tripPolyline', {
                type: 'FeatureCollection',
                features: [{
                    type: 'Feature',
                    geometry: { type: 'LineString', coordinates: polylineData.coordinates },
                    properties: { color: polylineData.properties.color, casingColor: darkenColor(polylineData.properties.color, 0.45) },
                }],
            }, polylineSignature)
        }

        if (tripData?.stop_times) {
            const stopTimesWithCoordinates = tripData.stop_times
                .filter((stopTime: StopTime) => stopTime.stop?.lon != null && stopTime.stop?.lat != null)
            const stopIds = stopTimesWithCoordinates.map((stopTime: StopTime) => stopTime.stop.gtfs_id)

            hydrateStopCoordinates(stopIds)

            const tripStopsSignature = stopTimesWithCoordinates
                .map((stopTime: StopTime) => `${stopTime.stop.gtfs_id}:${stopTime.stop.lon}:${stopTime.stop.lat}:${stopTime.stop.background_color || ''}:${stopTime.stop.type || 'stop'}`)
                .join('|')

            setGeoJsonData('tripStops', {
                type: 'FeatureCollection',
                features: stopTimesWithCoordinates
                    .map((stopTime: StopTime, index: number) => {
                        const stopType = (stopTime.stop.type || 'stop').toLowerCase()
                        const agencyPrefix = (stopTime.stop.gtfs_id?.split(':')[0] || '').toLowerCase()
                        const iconAgency = AGENCY_ICON_WHITELIST.has(agencyPrefix) ? agencyPrefix : 'unknown'

                        return {
                            type: 'Feature',
                            geometry: {
                                type: 'Point',
                                coordinates: stopCoordinateCacheRef.current.get(stopTime.stop.gtfs_id) || [stopTime.stop.lon, stopTime.stop.lat],
                            },
                            properties: {
                                name: stopTime.stop.name,
                                id: stopTime.stop.gtfs_id,
                                isTerminal: index === 0 || index === stopTimesWithCoordinates.length - 1,
                                textColor: (stopTime.stop.background_color && stopTime.stop.background_color !== '#777777')
                                    ? stopTime.stop.background_color
                                    : (tripData.color || '#666666'),
                                icon: `${iconAgency}_${stopType}`,
                                fallback_icon: `unknown_${stopType}`,
                            },
                        }
                    }),
            }, tripStopsSignature)
        }
    }, [polylineData, tripData, setGeoJsonData, hydrateStopCoordinates])

    useEffect(() => {
        if (mapLoaded && polylineData) {
            updatePolylineLayer()
        }
    }, [mapLoaded, updatePolylineLayer, polylineData])

    // Vehicle locations

    const updateVehicleLayer = useCallback(async () => {
        if (vehicleLocationData) {
            // Get unique colors and register images
            const uniqueColors = [...new Set(vehicleLocationData.map((v: VehicleLocation) => normalizeVehicleColor(v.color)))] as string[]
            const mapInstance = map?.current
            if (mapInstance) {
                await registerVehicleImages(mapInstance, uniqueColors)
                // Track which images we registered for cleanup
                uniqueColors.forEach((color) => {
                    const imageId = `vehicle-${colorToImageId(color)}`
                    registeredVehicleImagesRef.current.add(imageId)
                })
            }

            const vehicleSignature = vehicleLocationData
                .map((vehicle: VehicleLocation) => `${vehicle.lon}:${vehicle.lat}:${vehicle.heading || 0}:${normalizeVehicleColor(vehicle.color)}`)
                .join('|')

            setGeoJsonData('vehicles', {
                type: 'FeatureCollection',
                features: vehicleLocationData.map((vehicle: VehicleLocation) => ({
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: [vehicle.lon, vehicle.lat] },
                    properties: {
                        color: normalizeVehicleColor(vehicle.color),
                        color_id: colorToImageId(normalizeVehicleColor(vehicle.color)),
                        heading: vehicle.heading ?? 0,
                    },
                })),
            }, vehicleSignature)
        }

        const mapInstance = map?.current
        if (mapInstance && vehicleLocationData) {
            if (isFollowing && vehicleLocationData.length > 0) {
                mapInstance.flyTo({
                    center: [vehicleLocationData[0].lon, vehicleLocationData[0].lat],
                    zoom: 15,
                })
            }
        }
    }, [vehicleLocationData, isFollowing, map, setGeoJsonData])

    useEffect(() => {
        if (mapLoaded && vehicleLocationData) {
            updateVehicleLayer()
        }
    }, [mapLoaded, updateVehicleLayer, vehicleLocationData])

    const menuItems = isToday
        ? [
                <MenuItem onClick={() => setIsFollowing(f => !f)} key="follow">
                    <Checkbox checked={isFollowing} />
                    {' '}
                    {t('Follow vehicle')}
                </MenuItem>,
            ]
        : []

    // Trip detail map focus
    useEffect(() => {
        const mapInstance = map?.current
        if (!mapLoaded || !mapInstance) return

        applyPaintRules(mapInstance, [
            { prefix: 'stops-symbol-', property: 'icon-opacity', value: TRIP_BACKGROUND_ICON_OPACITY_EXPRESSION },
            { prefix: 'stops-symbol-', property: 'text-opacity', value: TRIP_BACKGROUND_ICON_OPACITY_EXPRESSION },
            { prefix: 'micromobility-icon-', property: 'icon-opacity', value: TRIP_BACKGROUND_ICON_OPACITY_EXPRESSION },
            { prefix: 'micromobility-detail-', property: 'icon-opacity', value: TRIP_BACKGROUND_ICON_OPACITY_EXPRESSION },
            { prefix: 'micromobility-detail-', property: 'text-opacity', value: TRIP_BACKGROUND_ICON_OPACITY_EXPRESSION },
            { prefix: 'parkings-circles', property: 'icon-opacity', value: TRIP_BACKGROUND_ICON_OPACITY_EXPRESSION },
        ])

        applyLayoutRules(mapInstance, [
            { prefix: 'stops-circles-', property: 'visibility', value: 'none' },
            { prefix: 'stops-symbol-', property: 'icon-size', value: TRIP_BACKGROUND_ICON_SIZE_EXPRESSION },
            { prefix: 'micromobility-icon-', property: 'icon-size', value: TRIP_BACKGROUND_ICON_SIZE_EXPRESSION },
            { prefix: 'micromobility-detail-', property: 'icon-size', value: TRIP_BACKGROUND_ICON_SIZE_EXPRESSION },
            {
                prefix: 'micromobility-detail-',
                property: 'text-size',
                value: ['step', ['zoom'], 0, TRIP_ICON_EXPAND_ZOOM, DEFAULT_MICROMOBILITY_DETAIL_TEXT_SIZE],
            },
            { prefix: 'parkings-circles', property: 'icon-size', value: TRIP_BACKGROUND_ICON_SIZE_EXPRESSION },
            { prefix: 'parkings-details', property: 'visibility', value: 'none' },
            { prefix: 'planView', property: 'visibility', value: 'none' },
            { prefix: 'planVehicleLocations', property: 'visibility', value: 'none' },
        ])

        return () => {
            applyPaintRules(mapInstance, [
                { prefix: 'stops-symbol-', property: 'icon-opacity', value: 1 },
                { prefix: 'stops-symbol-', property: 'text-opacity', value: 1 },
                { prefix: 'micromobility-icon-', property: 'icon-opacity', value: 1 },
                { prefix: 'micromobility-detail-', property: 'icon-opacity', value: 1 },
                { prefix: 'micromobility-detail-', property: 'text-opacity', value: 1 },
                { prefix: 'parkings-circles', property: 'icon-opacity', value: 1 },
            ])

            applyLayoutRules(mapInstance, [
                { prefix: 'stops-circles-', property: 'visibility', value: 'visible' },
                { prefix: 'stops-symbol-', property: 'icon-size', value: DEFAULT_STOP_ICON_SIZE },
                { prefix: 'micromobility-icon-', property: 'icon-size', value: DEFAULT_STOP_ICON_SIZE },
                { prefix: 'micromobility-detail-', property: 'icon-size', value: ICON_BASE_SIZE * 1 },
                { prefix: 'micromobility-detail-', property: 'text-size', value: DEFAULT_MICROMOBILITY_DETAIL_TEXT_SIZE },
                { prefix: 'parkings-circles', property: 'icon-size', value: DEFAULT_PARKING_ICON_SIZE },
                { prefix: 'micromobility-icon-', property: 'visibility', value: 'visible' },
                { prefix: 'micromobility-detail-', property: 'visibility', value: 'visible' },
                { prefix: 'parkings-circles', property: 'visibility', value: 'visible' },
                { prefix: 'parkings-details', property: 'visibility', value: 'visible' },
                { prefix: 'planView', property: 'visibility', value: 'visible' },
                { prefix: 'planVehicleLocations', property: 'visibility', value: 'visible' },
            ])

            setGeoJsonData(SOURCE_TRIP_POLYLINE, { type: 'FeatureCollection', features: [] }, 'cleared')
            setGeoJsonData(SOURCE_TRIP_STOPS, { type: 'FeatureCollection', features: [] }, 'cleared')
            setGeoJsonData(SOURCE_VEHICLES, { type: 'FeatureCollection', features: [] }, 'cleared')
        }
    }, [mapLoaded, map, setGeoJsonData])

    if (tripDataLoading) {
        return (
            <AutomaticSheet>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: window.innerHeight / 2 }}>
                    <CircularProgress />
                </Box>
            </AutomaticSheet>
        )
    }

    if (tripDataError || !tripData) {
        return (
            <AutomaticSheet>
                <Stack sx={{ p: 5 }}>{t('Error loading trip details.')}</Stack>
            </AutomaticSheet>
        )
    }

    const hasAlerts = (trip: { stop_times?: StopTime[] } | undefined) => trip?.stop_times?.some((stopTime: StopTime) => stopTime.alerts.length > 0)

    return (
        <AutomaticSheet>
            <Stack>
                <SheetHeader
                    heading={(
                        <>
                            <LineChip
                                text={tripData.route_short_name || ''}
                                color={tripData.color || '#666'}
                                textColor={tripData.text_color || '#fff'}
                                extraStyles={{ marginRight: 5 }}
                            />
                            {tripData?.trip_headsign}
                            {hasAlerts(tripData) && <BusAlertIcon sx={{ color: 'orange' }} />}
                        </>
                    )}
                    menuItems={menuItems}
                >
                    {isToday && (vehicleLocationData && vehicleLocationData.length > 0) && (
                        <Typography variant="subtitle2">
                            {`${t('Vehicle')}: ${vehicleLocationData.map((v: VehicleLocation) => v.vehicle.plate || v.vehicle.id).join(', ')}`}
                        </Typography>
                    )}
                </SheetHeader>
                <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <AlertDrawer tripData={tripData} />
                    <TripArrivals tripData={tripData} isToday={isToday} />
                </Box>
            </Stack>
        </AutomaticSheet>
    )
}

function Trip() {
    const params = useParams({ strict: false }) as { tripId: string, date?: string }
    const today = DateTime.local({ zone: 'Europe/Ljubljana' }).toFormat('yyyyMMdd')
    const queryDate = params.date && /^\d{8}$/.test(params.date) ? params.date : today
    return <TripPage tripId={params.tripId} queryDate={queryDate} />
}

export default Trip
